import { useMutation } from '@tanstack/react-query';
import type { CellContext } from '@tanstack/react-table';
import type { AxiosError } from 'axios';
import { History, Link, ListCollapse, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { UpdateOneRequestItem } from '@/pages/PurchasesContainer/RequestPerOffer/api/OffersRequest.api';

// Pre-define static styles to improve rendering performance
const STYLES = {
  container:
    'flex h-16 w-full flex-col items-center justify-center rounded-lg border border-primary/20 bg-white p-1 shadow-sm dark:bg-gray-900',
  buttonsRow: 'flex w-full items-center justify-center gap-1 mb-1',
  button: 'h-6 w-full rounded-md',
  buttonAction:
    'hover:bg-primary/10 hover:text-primary dark:hover:bg-primary/20 focus-visible:ring-1 focus-visible:ring-primary',
  buttonDelete:
    'hover:bg-destructive/10 hover:text-destructive dark:hover:bg-destructive/20 focus-visible:ring-1 focus-visible:ring-destructive disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
};

type Props = {
  handleOpenEditDialog: (item: IOneItemInPrepareForRequest) => void;
  handleShowItemGroup: (value: boolean) => void;
  handleSelectItem: (item: IOneItemInPrepareForRequest) => void;
  handleUpdateOneItemLink: (item: IOneItemInPrepareForRequest) => void;
  handleShowPurchaseHistory: (value: boolean) => void;
  CellContext: CellContext<IOneItemInPrepareForRequest, unknown>;
  handleLoadingStage: (
    value: boolean,
    type: 'check' | 'upload' | 'update' | 'main',
  ) => void;
};

function ActionsCol({
  handleOpenEditDialog,
  handleSelectItem,
  handleShowItemGroup,
  handleUpdateOneItemLink,
  handleShowPurchaseHistory,
  handleLoadingStage,
  CellContext,
}: Props) {
  const item = CellContext.row.original;
  const { index } = CellContext.row;
  const table = CellContext.table;

  const resetItem = () => {
    table.options.meta?.updateData(index, 'Qty', 0);
    table.options.meta?.updateData(index, 'DiscountEdit', item?.Discount || 0);
    table.options.meta?.updateData(index, 'TotalAfterDiscount', 0);

    // Add a redundant update to force the summary to recalculate
    setTimeout(() => {
      table.options.meta?.updateData(index, 'Qty', 0);
    }, 0);
  };

  const updateItemLinkMutation = useMutation({
    mutationFn: UpdateOneRequestItem,
    onSuccess: (data) => {
      handleUpdateOneItemLink(data);
      toast.success('🎉 الصنف اتحدث! 😂💸', {
        position: 'bottom-center',
      });
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      toast.error(`${err?.response?.data?.message}`, {
        position: 'bottom-center',
      });
    },
  });

  return (
    <div className={STYLES.container}>
      {/* First row of buttons */}
      <div className={STYLES.buttonsRow}>
        <Button
          title="احصائيات الصنف"
          variant="ghost"
          size="icon"
          className={cn(STYLES.button, STYLES.buttonAction)}
          onClick={() => {
            if (item?.HealthyItemID) {
              handleSelectItem(item);
              handleShowItemGroup(true);
            }
          }}
        >
          <ListCollapse className="h-4 w-4" />
        </Button>

        <Button
          title="تعديل ربط الصنف"
          variant="ghost"
          size="icon"
          className={cn(STYLES.button, STYLES.buttonAction)}
          onClick={() => {
            if (item) {
              handleOpenEditDialog(item);
            }
          }}
        >
          <Link className="h-4 w-4" />
        </Button>

        <Button
          title="تاريخ المشتريات"
          variant="ghost"
          size="icon"
          className={cn(STYLES.button, STYLES.buttonAction)}
          onClick={() => {
            if (item?.HealthyItemID) {
              handleSelectItem(item);
              handleShowPurchaseHistory(true);
            } else {
              toast.error('يجب ربط الصنف أولاً لعرض تاريخ المشتريات', {
                position: 'bottom-center',
              });
            }
          }}
        >
          <History className="h-4 w-4" />
        </Button>
      </div>

      {/* Second row of buttons */}
      <div className={STYLES.buttonsRow}>
        <Button
          title="تحديث الصنف"
          variant="ghost"
          size="icon"
          className={cn(STYLES.button, STYLES.buttonAction)}
          onClick={async () => {
            if (!item?.HealthyItemID) {
              toast.error(
                '🛑 يا نجم، بتحاول تحدث صنف مش مربوط؟! 🔗 اربطه الأول وبعدين تعالى كلمني! 😂',
                {
                  position: 'bottom-center',
                },
              );
              return;
            } else if (
              Math.abs((item?.Price || 0) - (item?.healthyItemPrice || 0)) > 0.5
            ) {
              toast.error(
                '🧐 يا مدير! سعر المورد غير سعرنا! 🤨 إضبط الدنيا قبل ما الحسابات تولع 🔥😂',
                {
                  position: 'bottom-center',
                },
              );
            } else {
              handleLoadingStage(true, 'update');
              await updateItemLinkMutation.mutateAsync({
                id: item?.itemBodyID,
                itemID: item?.HealthyItemID,
              });
            }
          }}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>

        <Button
          title="مسح الصنف من الطلبية"
          variant="ghost"
          size="icon"
          disabled={item?.Qty === 0}
          className={cn(STYLES.button, STYLES.buttonDelete)}
          onClick={() => {
            if (item?.Qty > 0) {
              resetItem();
            }
          }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default ActionsCol;
